import _fetch, { downBlob } from "../../share/net/fetch"
import _Request from "../../share/net/request"
import { getNetStatus } from "../../utils/constant"
import { logWarnMsg } from "../../utils/throwErrorMsg"

const request = new _Request({
  GET: _fetch.get,
  POST: _fetch.post,
  DELETE: _fetch.delete,
  PUT: _fetch.put,
  UPLOAD: _fetch.upload,
})

let serviceCallCfgFn: ServiceCallConfig[]
let serviceCallEnv = "prod"
let migrateConfig = {}

/**
 * @dtsModule max.net
 * @dtsNamespace net
 * @dtsDesc net Net
 * @dtsInterface
 */
export const net = {
  init(
    serviceCallCfg: { getConfig: ServiceCallConfig },
    _serviceCallEnv?: string,
    _migrateConfig?: any
  ) {
    if (!serviceCallCfgFn) serviceCallCfgFn = []
    serviceCallCfgFn.push(serviceCallCfg.getConfig)
    if (_serviceCallEnv) serviceCallEnv = _serviceCallEnv
    if (_migrateConfig) migrateConfig = _migrateConfig
  },

  /**
   * @dtsExport
   * @dtsName init
   * @dtsNoTypeReplace
   * Global configuration net config and interceptor
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.net.init&lang=%{locale}%)
   */
  config(
    config?: NetConfig,
    interceptor?: InitIntercept<NetRequest, NetResult<any>>
  ) {
    request.config(config, interceptor)
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   * Network GET request
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.net.get&lang=%{locale}%)
   */
  async get<T = any>(url: string, config?: NetConfig & { type?: "GET" }): Promise<NetResult<T>> {
    const { data = "" } = config || {}
    const result = await request.preSend({
      url,
      ...config,
      data,
      method: "GET",
    })
    return result
  },

  /**
   * @dtsExport
   * @dtsNoTypeReplace
   * Network POST request
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.net.post&lang=%{locale}%)
   */
  async post<T = any>(url: string, config?: NetConfig & { type?: "POST" }): Promise<NetResult<T>> {
    const { data = {} } = config || {}
    const result = await request.preSend({
      url,
      ...config,
      data,
      method: "POST",
    })
    return result
  },

  /**
  * @dtsExport
  * @dtsNoTypeReplace
  * Network request
  * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.net.request&lang=%{locale}%)
  */
  async request<T = any>(
    url: string,
    config?: NetConfig & { type?: "GET" | "POST" }
  ): Promise<NetResult<T>> {
    if (config?.type == "GET") {
      return net.get(url, config as NetConfig & { type?: "GET" })
    }
    return net.post(url, config as NetConfig & { type?: "POST" })
  },
  /**
   * @dtsExport
   * @dtsNoTypeReplace
   * File upload
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.net.upload&lang=%{locale}%)
  */
  async upload<T = any>(
    url: string,
    config?: NetConfig & {
      method?: string
      fileList?: any[]
      formData?: any
    } & {
      interceptor?: (<
        T = NetConfig & {
          fileList: any[]
          formData?: any
        }
      >(
        params: T
      ) => T)[]
    }
  ): Promise<NetResult<T>> {
    const result = await request.preSend({ url, ...config, method: "UPLOAD" })
    return result
  },
  /**
   * @dtsExport
   * File download
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.net.download&lang=%{locale}%)
  */
  download(config: { url: string }) {
    if (!config?.url?.length) {
      throw Error("can not found url")
    }
    // 直接下载
    window.open(config.url)
    // todo blob下载,暂时没需求 先搁置后续再发开
    // config.responseType = 'blob'
    // const { body } = await request.preSend({ url, ...config })
    // return downBlob(body)
  },
  /**
     * @dtsExport
     * @dtsNoTypeReplace
     * Network PUT request
     * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.net.put&lang=%{locale}%)
    */
  async put<T = any>(
    url: string,
    config?: NetConfig & {
      fileList?: any[]
      formData?: any
    }
  ): Promise<NetResult<T>> {
    const result = await request.preSend({ url, ...config, method: "PUT" })
    return result
  },
  /**
     * @dtsExport
     * @dtsNoTypeReplace
     * Network DELETE request
     * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.net.delete&lang=%{locale}%)
    */
  async delete<T = any>(
    url: string,
    config?: NetConfig
  ): Promise<NetResult<T>> {
    const result = await request.preSend({ url, ...config, method: "DELETE" })
    return result
  },
  /**
   * 网关请求
   * @param serviceCode
   * @param _params
   * @param isGetway
   * @returns
   */
  serviceCall(serviceCode, _params, isGetway, getAll) {
    return new Promise(async (resolve, reject) => {
      if (!serviceCallCfgFn?.length) {
        reject("please INIT serviceCall config")
        return
      }
      const configFn = serviceCallCfgFn.find((fn) => fn(serviceCode)?.params)
      if (typeof configFn !== "function") {
        reject(serviceCode + " function not found")
        return
      }
      const options = typeof isGetway === "object" ? isGetway : {}

      const { params: services, ...args } = configFn!(serviceCode)
      if (!services) {
        reject(serviceCode + " not found")
        return
      }
      // 如果服务是官方服务，并且用户传入了region，则使用用户传入的region
      let customRegion = ""
      if (services.isOfficial === 1 && options.region) {
        customRegion = options.region
      }
      const result = await request.getway(
        services,
        serviceCode,
        _params,
        {
          ...args,
          isGetway,
          getAll,
          serviceCallEnv,
          ...options,
          customRegion,
          isPublic: services.isPublic
        },
        migrateConfig
      )
      resolve(result)
    })
  },
  addListener(fn) {
    logWarnMsg('net.addListener')
    // let rateTime =
    //   config?.rateTime !== undefined
    //     ? Math.floor(Math.abs(config?.rateTime))
    //     : 5
    // rateTime = rateTime < 0 ? 1 : rateTime
    // netListener.init(fn, {
    //   rateTime: rateTime * 1000,
    //   speed: config?.speed,
    // })
  },
  removeListener() {
    // netListener.off()
    logWarnMsg('net.removeListener')
  },
  enableCache(param) {
    return false
  },
  clearCache() {
    logWarnMsg('net.clearCache')
  },
  /**
  * @dtsExport
  * Get network status
  * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.net.getNetStatus&lang=%{locale}%)
  */
  async getNetStatus(): Promise<TNetStatus> {
    return getNetStatus()
  },
  listenerNet(online: () => void, offline: () => void): void {
    window.addEventListener('online', online)
    window.addEventListener('offline', offline)
  }
}
